<script>
  import { createEventDispatcher, onMount } from 'svelte'
  import { getTemplates, deleteTemplate, getTemplateExercises, createWorkoutFromTemplate, updateTemplateUsage } from '../services/templates.js'
  import { saveWorkoutWithExercises } from '../services/db.js'
  import { format } from 'date-fns'
  import TemplateEditor from './TemplateEditor.svelte'

  const dispatch = createEventDispatcher()

  let templates = []
  let isLoading = true
  let searchQuery = ''
  let showEditor = false
  let editorMode = 'create'
  let editingTemplateId = null
  let expandedTemplates = {}

  onMount(async () => {
    await loadTemplates()
  })

  async function loadTemplates() {
    isLoading = true
    try {
      templates = await getTemplates({ search: searchQuery })
    } catch (error) {
      console.error('Failed to load templates:', error)
      alert('載入模板失敗')
    } finally {
      isLoading = false
    }
  }

  function handleSearch() {
    loadTemplates()
  }

  function handleAddTemplate() {
    editorMode = 'create'
    editingTemplateId = null
    showEditor = true
  }

  function handleEditTemplate(templateId) {
    editorMode = 'edit'
    editingTemplateId = templateId
    showEditor = true
  }

  async function handleDeleteTemplate(template) {
    if (!confirm(`確定要刪除模板「${template.name}」嗎？\n\n此操作無法復原。`)) {
      return
    }

    try {
      await deleteTemplate(template.id)
      showToast('模板已刪除', 'success')
      await loadTemplates()
    } catch (error) {
      console.error('Delete failed:', error)
      showToast('刪除失敗，請稍後再試', 'error')
    }
  }

  function handleStartWorkout(template) {
    // Navigate to workout form with template info
    // WorkoutForm will handle loading the template and creating the workout
    dispatch('navigate', {
      view: 'workoutForm',
      options: {
        mode: 'create',
        templateId: template.id
      }
    })
  }

  async function toggleExercises(templateId) {
    if (expandedTemplates[templateId]) {
      expandedTemplates[templateId] = null
    } else {
      try {
        const exercises = await getTemplateExercises(templateId)
        expandedTemplates[templateId] = exercises
      } catch (error) {
        console.error('Failed to load template exercises:', error)
      }
    }
    expandedTemplates = expandedTemplates
  }

  function handleEditorClose() {
    showEditor = false
    editingTemplateId = null
  }

  async function handleEditorSave() {
    showEditor = false
    editingTemplateId = null
    await loadTemplates()
  }

  function formatSetsDisplay(exercise) {
    if (!Array.isArray(exercise.sets) || exercise.sets.length === 0) {
      return '未設定組數'
    }

    const totalSets = exercise.sets.length

    // Check if all sets have same weight and reps
    const allSame = exercise.sets.every(s =>
      s.weight === exercise.sets[0].weight && s.reps === exercise.sets[0].reps
    )

    if (allSame) {
      const set = exercise.sets[0]
      const parts = [`${totalSets} 組`]
      if (set.reps) parts.push(`${set.reps} 次`)
      if (set.weight) parts.push(`${set.weight} kg`)
      return parts.join(' × ')
    }

    return `${totalSets} 組（數值不同）`
  }

  function showToast(message, type = 'success') {
    const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500'
    const toast = document.createElement('div')
    toast.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in`
    toast.textContent = message
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 2000)
  }

  $: filteredTemplates = templates
</script>

{#if showEditor}
  <TemplateEditor
    mode={editorMode}
    templateId={editingTemplateId}
    on:close={handleEditorClose}
    on:save={handleEditorSave}
  />
{:else}
  <div class="templates-container">
    <!-- Header -->
    <div class="mb-6">
      <h2 class="text-2xl font-bold text-gray-800 mb-4">訓練模板</h2>

      <!-- Search Bar -->
      <div class="flex gap-2 mb-4">
        <input
          type="text"
          class="input-field flex-1"
          placeholder="搜尋模板名稱或描述..."
          bind:value={searchQuery}
          on:input={handleSearch}
        />
        <button
          type="button"
          class="btn btn-primary"
          on:click={handleAddTemplate}
        >
          + 新增模板
        </button>
      </div>

      <p class="text-sm text-gray-600">
        {#if isLoading}
          載入中...
        {:else}
          共 {filteredTemplates.length} 個模板
        {/if}
      </p>
    </div>

    <!-- Template List -->
    <div class="space-y-4">
      {#if isLoading}
        <div class="text-center py-12">
          <div class="spinner mx-auto mb-4"></div>
          <p class="text-gray-600">載入中...</p>
        </div>
      {:else if filteredTemplates.length === 0}
        <div class="card text-center py-12">
          <div class="text-6xl mb-4">📋</div>
          <h3 class="text-xl font-semibold text-gray-800 mb-2">
            {searchQuery ? '找不到符合的模板' : '尚無訓練模板'}
          </h3>
          <p class="text-gray-600 mb-4">
            {searchQuery ? '試試其他關鍵字' : '建立模板讓訓練更有效率'}
          </p>
          {#if !searchQuery}
            <button type="button" class="btn btn-primary" on:click={handleAddTemplate}>
              建立第一個模板
            </button>
          {/if}
        </div>
      {:else}
        {#each filteredTemplates as template (template.id)}
          <div class="card hover:shadow-lg transition-shadow template-card">
            <div class="flex justify-between items-start mb-3">
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-2">
                  <h3 class="text-xl font-semibold text-gray-800">{template.name}</h3>
                  {#if !template.synced}
                    <span class="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">待同步</span>
                  {/if}
                </div>

                {#if template.description}
                  <p class="text-sm text-gray-600 mb-2">{template.description}</p>
                {/if}

                <div class="flex flex-wrap gap-3 text-sm text-gray-500">
                  <span>📊 使用次數: {template.use_count || 0}</span>
                  {#if template.last_used_at}
                    <span>🕐 上次使用: {format(new Date(template.last_used_at), 'yyyy/MM/dd')}</span>
                  {/if}
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-2 mb-3">
              <button
                type="button"
                class="btn btn-primary flex-1"
                on:click={() => handleStartWorkout(template)}
              >
                開始訓練
              </button>
              <button
                type="button"
                class="btn btn-secondary"
                on:click={() => handleEditTemplate(template.id)}
              >
                編輯
              </button>
              <button
                type="button"
                class="text-red-600 hover:text-red-800 px-3"
                on:click={() => handleDeleteTemplate(template)}
              >
                刪除
              </button>
            </div>

            <!-- Toggle Exercises -->
            <button
              type="button"
              class="text-sm text-gray-500 cursor-pointer hover:text-gray-700 w-full text-left"
              on:click={() => toggleExercises(template.id)}
            >
              {expandedTemplates[template.id] ? '隱藏運動項目 ↑' : '查看運動項目 →'}
            </button>

            {#if expandedTemplates[template.id]}
              <div class="exercises-list mt-3 bg-gray-50 rounded-lg p-3">
                <div class="text-sm font-medium text-gray-700 mb-2">運動項目：</div>
                {#if expandedTemplates[template.id].length === 0}
                  <div class="text-sm text-gray-500">此模板無運動項目</div>
                {:else}
                  <div class="space-y-2">
                    {#each expandedTemplates[template.id] as ex, index}
                      <div class="bg-white p-3 rounded text-sm">
                        <div class="font-medium text-gray-800 mb-1">{index + 1}. {ex.exercise_name}</div>
                        <div class="text-gray-600 text-xs">
                          {formatSetsDisplay(ex)}
                          {ex.rest_time ? ` • 休息 ${ex.rest_time}秒` : ''}
                        </div>
                        {#if ex.notes}
                          <div class="text-gray-500 text-xs mt-1 italic">💡 {ex.notes}</div>
                        {/if}
                        {#if Array.isArray(ex.sets) && ex.sets.length > 0}
                          <div class="mt-2 space-y-1">
                            {#each ex.sets as set, setIndex}
                              <div class="flex items-center gap-2 text-xs text-gray-500">
                                <span class="w-4">{setIndex + 1}.</span>
                                <span>
                                  {#if set.weight !== null || set.reps !== null}
                                    {set.weight !== null ? `${set.weight} kg` : '?'} × {set.reps !== null ? `${set.reps} 次` : '?'}
                                  {:else}
                                    未設定數值
                                  {/if}
                                </span>
                              </div>
                            {/each}
                          </div>
                        {/if}
                      </div>
                    {/each}
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        {/each}
      {/if}
    </div>
  </div>
{/if}

<style>
  .template-card {
    transition: all 0.2s;
  }
</style>
